"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertTriangleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { DeleteProjectInput } from "./schema";

const DeleteProjectConfirmQuery = graphql(`
  query DeleteProjectConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on IndexWorkerProject {
        id
        name
        description
      }
      ... on SearchAnalyticsProject {
        id
        name
        description
      }
      ... on UserAnalyticsProject {
        id
        name
        description
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

/**
 * プロジェクト削除の確認 UI。
 * ユーザーに詳細を提示し、危険操作であることを明示して承認可否を求める。
 */
export function DeleteProjectConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const router = useRouter();
  const isTarget = isToolUIPart(part) && getToolName(part) === "delete_project";
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ?? {}) as Partial<DeleteProjectInput>;
  const projectId = input.projectId ?? "";

  const [{ data, fetching }] = useQuery({
    query: DeleteProjectConfirmQuery,
    variables: { id: projectId },
    pause: !(isTarget && isInputAvailable && projectId),
  });

  if (!isTarget || !isInputAvailable) return null;

  const projectNode = data?.node;
  const canRenderProject =
    projectNode?.__typename === "IndexWorkerProject" ||
    projectNode?.__typename === "SearchAnalyticsProject" ||
    projectNode?.__typename === "UserAnalyticsProject"
      ? projectNode
      : null;

  return (
    <div className="p-4 text-sm space-y-4">
      <div>
        <div className="mb-2 font-medium">
          <Trans>Do you want to delete this project?</Trans>
        </div>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <Trans>Name</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-32" />
            ) : (
              <span className="font-medium">
                {canRenderProject?.name ?? <Trans>Unknown project</Trans>}
              </span>
            )}
          </li>
          <li>
            <Trans>Project type</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <span className="font-medium">
                {renderProjectType(canRenderProject?.__typename)}
              </span>
            )}
          </li>
          {canRenderProject?.description ? (
            <li>
              <Trans>Description</Trans>: {canRenderProject.description}
            </li>
          ) : null}
        </ul>
      </div>
      <Alert variant="destructive" className="text-xs">
        <AlertTriangleIcon className="h-4 w-4" />
        <AlertTitle>
          <Trans>This action is irreversible</Trans>
        </AlertTitle>
        <AlertDescription>
          <Trans>
            Deleting a project also removes its analytics configuration and any
            related saved views, including all watch rules in the project.
          </Trans>
        </AlertDescription>
      </Alert>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
          variant="destructive"
          onSuccess={() => router.refresh()}
        >
          <Trans>Delete</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}

/**
 * プロジェクト種別を i18n 文言に変換する。
 */
function renderProjectType(typename?: string) {
  if (typename === "SearchAnalyticsProject") {
    return <Trans>Search Analytics project</Trans>;
  }
  if (typename === "UserAnalyticsProject") {
    return <Trans>User Analytics project</Trans>;
  }
  if (typename === "IndexWorkerProject") {
    return <Trans>Index Worker project</Trans>;
  }
  return <Trans>Unknown type</Trans>;
}
